import React from "react";
import { Heart, MessageCircle, Instagram } from "lucide-react";
import { useLang } from "@/contexts/LanguageContext";
import { communityImages } from "@/lib/translations";
import Reveal from "@/components/Reveal";

const HANDLES = [
  "@isabelle.golf",
  "@camilla_a",
  "@theresa.tee",
  "@nadya.swing",
  "@léa.fairway",
  "@maya.club",
  "@valentina_g",
  "@chiara.par",
  "@hannah_links",
];

const tournaments = [
  { en: "MALY Open · Côte d'Azur", de: "MALY Open · Côte d'Azur", date: "May 2026" },
  { en: "The Atelier Cup · Lake Como", de: "Atelier Cup · Lake Como", date: "Jul 2026" },
  { en: "Sunset Invitational · Marrakech", de: "Sunset Invitational · Marrakech", date: "Sep 2026" },
];

export default function Community() {
  const { lang, t } = useLang();

  // Combine community images with duplicates for 9-grid
  const grid = [...communityImages, ...communityImages].slice(0, 9);

  return (
    <div data-testid="community-page" className="bg-maly-cream">
      {/* Hero */}
      <section className="pt-40 lg:pt-48 pb-20 px-6 lg:px-12 text-center">
        <Reveal>
          <div className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-navy/60 mb-8">
            {t.community.overline}
          </div>
        </Reveal>
        <Reveal delay={100}>
          <h1 className="font-display text-5xl sm:text-6xl lg:text-8xl font-light tracking-[-0.02em] leading-[1.02] max-w-4xl mx-auto">
            {t.community.headline}
          </h1>
        </Reveal>
        <Reveal delay={200}>
          <p className="font-serif italic text-lg lg:text-xl opacity-70 mt-8 max-w-2xl mx-auto leading-relaxed">{t.community.sub}</p>
        </Reveal>
        <Reveal delay={300}>
          <a
            href="https://instagram.com"
            target="_blank"
            rel="noreferrer"
            data-testid="community-instagram"
            className="inline-flex items-center gap-3 mt-10 font-body text-[11px] tracking-[0.25em] uppercase font-medium link-underline"
          >
            <Instagram size={16} strokeWidth={1.4} /> @MALYofficial
          </a>
        </Reveal>
      </section>

      {/* Instagram grid */}
      <section className="px-6 lg:px-12 pb-32">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 lg:gap-5">
            {grid.map((img, i) => (
              <Reveal key={i} delay={i * 60}>
                <a
                  href="https://instagram.com"
                  target="_blank"
                  rel="noreferrer"
                  className="relative aspect-square block group img-zoom bg-maly-creamDeep"
                  data-testid={`ig-${i}`}
                >
                  <img src={img} alt={`Community ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  <div className="absolute inset-0 bg-maly-navy/60 opacity-0 group-hover:opacity-100 transition-opacity duration-500 flex flex-col items-center justify-center text-maly-cream gap-2">
                    <div className="font-body text-[10px] tracking-[0.3em] uppercase">{HANDLES[i % HANDLES.length]}</div>
                    <div className="flex items-center gap-5 text-sm">
                      <span className="flex items-center gap-1"><Heart size={14} strokeWidth={1.5} /> {Math.floor(Math.random() * 800) + 200}</span>
                      <span className="flex items-center gap-1"><MessageCircle size={14} strokeWidth={1.5} /> {Math.floor(Math.random() * 40) + 5}</span>
                    </div>
                  </div>
                </a>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Tournaments / events */}
      <section className="py-32 lg:py-40 px-6 lg:px-12 bg-maly-navy text-maly-cream relative grain">
        <div className="max-w-[1400px] mx-auto">
          <Reveal>
            <div className="font-body text-[11px] tracking-[0.3em] uppercase opacity-60 mb-6">
              {lang === "en" ? "Tournaments & Events" : "Turniere & Events"}
            </div>
          </Reveal>
          <Reveal delay={100}>
            <h2 className="font-display text-4xl lg:text-6xl font-light tracking-[-0.01em] max-w-3xl leading-[1.05]">
              {lang === "en" ? "Where the community plays." : "Wo die Community spielt."}
            </h2>
          </Reveal>

          <ul className="mt-20 divide-y divide-maly-cream/15">
            {tournaments.map((tour, i) => (
              <Reveal key={i} delay={i * 100}>
                <li className="flex items-center justify-between py-8 group cursor-pointer">
                  <div className="flex items-baseline gap-8">
                    <span className="font-body text-[11px] tracking-[0.3em] uppercase text-maly-gold">0{i + 1}</span>
                    <h3 className="font-display text-2xl lg:text-4xl font-light group-hover:italic transition-all duration-500">{tour[lang]}</h3>
                  </div>
                  <span className="font-body text-sm tracking-[0.15em] opacity-70">{tour.date}</span>
                </li>
              </Reveal>
            ))}
          </ul>
        </div>
      </section>
    </div>
  );
}
